import { DOCUMENT } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, Injector, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { IdleTimeoutService } from '../../app/shared/services/idle-timeout.service';

/**
 * "You are about to be signed out" dialog for the idle-session timeout. Rendered only
 * while {@link IdleTimeoutService.warningVisible} is true, so it never reaches the SSR
 * HTML. Only "Stay signed in" extends the session; background clicks do nothing.
 */
@Component({
  selector: 'app-idle-timeout-warning',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './idle-timeout-warning.component.html',
  styleUrl: './idle-timeout-warning.component.scss',
})
export class IdleTimeoutWarningComponent {
  protected readonly idle = inject(IdleTimeoutService);
  private readonly doc = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  /** The default action; receives focus when the dialog opens. */
  private readonly stayButton = viewChild<ElementRef<HTMLButtonElement>>('stayBtn');
  private readonly logoutButton = viewChild<ElementRef<HTMLButtonElement>>('logoutBtn');
  /** Element that had focus before the dialog opened, restored when it closes. */
  private previouslyFocused: HTMLElement | null = null;

  constructor() {
    // aria-modal alone does not move or contain keyboard focus: on open, focus goes to the safe
    // default ("Stay signed in"); on close it returns to where the user was.
    effect(() => {
      if (this.idle.warningVisible()) {
        const active = this.doc.activeElement;
        this.previouslyFocused = active instanceof HTMLElement ? active : null;
        afterNextRender(() => this.stayButton()?.nativeElement.focus(), { injector: this.injector });
      } else if (this.previouslyFocused) {
        this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }
    });
  }

  /** Keeps Tab inside the dialog's two buttons while it is open. */
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const first = this.logoutButton()?.nativeElement;
    const last = this.stayButton()?.nativeElement;
    if (!first || !last) return;
    const active = this.doc.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (active !== first && active !== last) {
      event.preventDefault();
      last.focus();
    }
  }

  /** Remaining time as `m:ss`. */
  protected readonly time = computed(() => {
    const total = Math.max(Math.ceil(this.idle.remainingMs() / 1000), 0);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  });
}
